import React from 'react';
import { observer } from 'mobx-react';

import { Link } from 'ui/base/link';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { LinkClickEvent, UnreachableFunction } from 'ui/base/types';

import { BasePath, CoreBasePath } from 'services/base/page_path';
import { Pangolin } from 'services/accounts/types';

import baseStyles from './core_navigation.module.css';
import Messages from './core_navigation_messages';

type CoreNavigationSyles = {
  container: string;
  content: string;
  iconContainer: string;
  link: string;
  homeIcon: string;
  devicesIcon: string;
  alertsIcon: string;
  moreIcon: string;
  text: string;
  selectedHomeIcon: string;
  selectedDevicesIcon: string;
  selectedAlertsIcon: string;
  selectedMoreIcon: string;
  selectedText: string;
};

const themedStyles: ThemedStyles<CoreNavigationSyles> = {
  [Theme.DAYLIGHT]: {
    container: baseStyles.daylightContainer,
    content: baseStyles.content,
    iconContainer: baseStyles.iconContainer,
    link: baseStyles.link,
    homeIcon: baseStyles.daylightHomeIcon,
    devicesIcon: baseStyles.daylightDevicesIcon,
    alertsIcon: baseStyles.daylightAlertsIcon,
    moreIcon: baseStyles.daylightMoreIcon,
    text: baseStyles.daylightText,
    selectedHomeIcon: baseStyles.selectedHomeIcon,
    selectedDevicesIcon: baseStyles.selectedDevicesIcon,
    selectedAlertsIcon: baseStyles.selectedAlertsIcon,
    selectedMoreIcon: baseStyles.selectedMoreIcon,
    selectedText: baseStyles.selectedText,
  },
  [Theme.MIDNIGHT]: {
    container: baseStyles.midnightContainer,
    content: baseStyles.content,
    iconContainer: baseStyles.iconContainer,
    link: baseStyles.link,
    homeIcon: baseStyles.midnightHomeIcon,
    devicesIcon: baseStyles.midnightDevicesIcon,
    alertsIcon: baseStyles.midnightAlertsIcon,
    moreIcon: baseStyles.midnightMoreIcon,
    text: baseStyles.midnightText,
    selectedHomeIcon: baseStyles.selectedHomeIcon,
    selectedDevicesIcon: baseStyles.selectedDevicesIcon,
    selectedAlertsIcon: baseStyles.selectedAlertsIcon,
    selectedMoreIcon: baseStyles.selectedMoreIcon,
    selectedText: baseStyles.selectedText,
  },
};

const getIconClass = (target: CoreBasePath, active: CoreBasePath, styles: CoreNavigationSyles) => {
  if (active === target) {
    return {
      [BasePath.HOME]: styles.selectedHomeIcon,
      [BasePath.DEVICES_LIST]: styles.selectedDevicesIcon,
      [BasePath.LIST_ISSUES]: styles.selectedAlertsIcon,
      [BasePath.MORE]: styles.selectedMoreIcon,
    }[target];
  }
  return {
    [BasePath.HOME]: styles.homeIcon,
    [BasePath.DEVICES_LIST]: styles.devicesIcon,
    [BasePath.LIST_ISSUES]: styles.alertsIcon,
    [BasePath.MORE]: styles.moreIcon,
  }[target];
};

const getTextClass = (target: CoreBasePath, active: CoreBasePath, styles: CoreNavigationSyles) => {
  if (active === target) {
    return styles.selectedText;
  } else {
    return styles.text;
  }
};

const getText = (target: CoreBasePath): string => {
  switch(target) {
    case BasePath.HOME:
      return Messages.home();
    case BasePath.DEVICES_LIST:
      return Messages.devices();
    case BasePath.LIST_ISSUES:
      return Messages.issues();
    case BasePath.MORE:
      return Messages.more();
    default:
      return UnreachableFunction(target);
  }
};

type NavigationLinkContentProps = {
  active: CoreBasePath;
  target: CoreBasePath;
  styles: CoreNavigationSyles;
};
const NavigationLinkContent = observer(({ active, target, styles }: NavigationLinkContentProps) => (
  <div className={styles.iconContainer}>
    <div className={getIconClass(target, active, styles)} />
    <div className={getTextClass(target, active, styles)}>{getText(target)}</div>
  </div>
));

type NavigationLinkProps = {
  pangolin: Pangolin;
  active: CoreBasePath;
  target: CoreBasePath;
  styles: CoreNavigationSyles;
};
const NavigationLink = observer(({ pangolin, active, target, styles }: NavigationLinkProps) => {
  const chosen = target === active;
  const path = { kind: target, pangolinID: pangolin.id };
  const className = styles.link;

  const onClick = React.useCallback((event: LinkClickEvent) => {
    if (chosen) event.preventDefault();
  }, [chosen]);

  return (
    <Link path={path} className={className} onClick={onClick} replace>
      <NavigationLinkContent active={active} target={target} styles={styles}/>
    </Link>
  );
});

export type BaseCoreNavigationProps = {
  pangolin: Pangolin;
  location: CoreBasePath;
};
export const BaseCoreNavigation = ({ pangolin, location }: BaseCoreNavigationProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <NavigationLink
          pangolin={pangolin}
          active={location}
          target={BasePath.HOME}
          styles={styles}
        />
        <NavigationLink
          pangolin={pangolin}
          active={location}
          target={BasePath.DEVICES_LIST}
          styles={styles}
        />
        <NavigationLink
          pangolin={pangolin}
          active={location}
          target={BasePath.LIST_ISSUES}
          styles={styles}
        />
        <NavigationLink
          pangolin={pangolin}
          active={location}
          target={BasePath.MORE}
          styles={styles}
        />
      </div>
    </div>
  );
};

type CoreNavigationProps = {
  pangolin: Pangolin;
  location: CoreBasePath;
};

export const CoreNavigation = observer(({ pangolin, location } : CoreNavigationProps) => {
  return (
    <BaseCoreNavigation
      pangolin={pangolin}
      location={location}
    />
  );
});
